import { Link } from 'react-router-dom';
import type { MarketSignal } from '../lib/types';
import { formatUsd } from '../lib/format';
import { Skeleton } from './Skeleton';
import { Disclaimer } from './Disclaimer';
import styles from './SignalCard.module.css';

/**
 * Older cached responses still carry the original action-style labels
 * ("BUY (SMALL)", "SELL / REDUCE"…); show them as the trend description they
 * always were, so the badge never reads as an instruction.
 */
const LEGACY_LABELS: Record<string, string> = {
  HOLD: 'NO SIGNAL',
  'HOLD / TAKE PROFIT': 'UPTREND · OVERBOUGHT',
  'BUY (SMALL)': 'UPTREND',
  'HOLD / DCA (TINY)': 'DOWNTREND · OVERSOLD',
  'SELL / REDUCE': 'DOWNTREND',
};
// Labels arrive upper-case from the API; shown in sentence case like every
// other label in the app ("UPTREND · OVERBOUGHT" -> "Uptrend · overbought").
const sentenceCase = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
const indicatorLabel = (label: string): string =>
  sentenceCase(LEGACY_LABELS[label] ?? label);

export function SignalCard({
  signal,
  isLoading,
  isError,
}: {
  signal: MarketSignal | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading && !signal) {
    return (
      <section className={styles.card} aria-label="Market signal loading">
        <Skeleton width="50%" height="1.5rem" />
        <Skeleton width="100%" height="23.5rem" />
      </section>
    );
  }

  if (isError || !signal) {
    return (
      <section className={styles.card} aria-label="Market signal unavailable">
        <p className={styles.unavailable} role="status">
          Market signal is temporarily unavailable. Wallet balance and history remain
          usable.
        </p>
      </section>
    );
  }

  return (
    <section className={styles.card} aria-labelledby="signal-heading">
      <div className={styles.headerRow}>
        <h2 id="signal-heading" className={styles.heading}>
          Technical indicator
        </h2>
        <span className={styles.actionBadge}>{indicatorLabel(signal.label)}</span>
      </div>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Price</span>
          <span>{formatUsd(signal.currentPrice)}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>EMA {signal.emaFastPeriod}</span>
          <span>{signal.emaFast !== null ? formatUsd(signal.emaFast) : '—'}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>EMA {signal.emaSlowPeriod}</span>
          <span>{signal.emaSlow !== null ? formatUsd(signal.emaSlow) : '—'}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>RSI {signal.rsiPeriod}</span>
          <span>{signal.rsi !== null ? signal.rsi.toFixed(1) : '—'}</span>
        </div>
      </div>

      <ul className={styles.rationale}>
        {signal.rationale.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <p className={styles.notARecommendation}>
        A backward-looking calculation, not a recommendation. See{' '}
        <Link to="/methodology">Methodology</Link>.
      </p>

      <Disclaimer summary="Not financial advice." text={signal.disclaimer} />
    </section>
  );
}
